import { LoadingOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { Select, Spin } from 'antd';
import { capitalize } from 'lodash';
import { useRef, useState } from 'react';
import { useWindowSize } from 'react-use';
import { isTabletBrowser } from '../../common/utils';
import ErrorComponent from '../../components/ErrorComponent';
import Sidebar from '../../components/Sidebar';
import useRouter from '../../hooks/useRouter';
import { Detection } from '../../types/reportAnnotationScreen.type';
import { GET_AREA_TYPES } from '../annotation-module/graphql/queries';
import { GET_FLOOR_PLAN_DETECTIONS } from '../vastu-assessment-report/graphql/queries';
import AnnotationScreen from './components/AnnotationScreen';
import FloorPlanDetectionCard from './components/FloorPlanDetectionCard';
import FloorPlanDetectionCardMobile from './components/FloorPlanDetectionCardMobile';
import VastuReportHeader from './components/VastuReportHeader';
import './styles/vastuReportPage.less';

export default function VastuReportPage() {
  const { params } = useRouter();
  const { width } = useWindowSize();
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [selectedDetectionId, setSelectedDetectionId] = useState<
    string | number | null
  >(null);
  const { data, loading, error } = useQuery(GET_FLOOR_PLAN_DETECTIONS, {
    variables: {
      data: {
        skip: 0,
        limit: 100,
        floorPlanId: params.id || '',
      },
    },
    fetchPolicy: 'network-only',
  });

  const detections = (data?.getDetectionsByFloorPlanWeb?.data || [])
    .filter((detection) => detection && detection?.bbox !== undefined)
    .filter(
      (detection, index, self) =>
        detection?.childId &&
        index ===
          self.findIndex((item) => item?.childId === detection?.childId),
    );

  const { data: staticData } = useQuery(GET_AREA_TYPES);
  const staticAreaTypes = staticData?.getFloorPlanStaticDataWeb?.data || [];

  const options = staticAreaTypes.map((item) => ({
    label: capitalize(item?.title || ''),
    value: item?.title,
  }));

  const filteredDetections = detections?.filter((d) =>
    selectedTypes?.length === 0
      ? true
      : selectedTypes.includes(d?.childType || ''),
  );

  const handleBoxClick = (id: string) => {
    setSelectedDetectionId(id);
    const cardElement = cardRefs?.current[id];
    if (cardElement) {
      cardElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      cardElement.classList.add('highlight');

      setTimeout(() => {
        cardElement.classList.remove('highlight');
      }, 1000);
    }
  };

  if (loading)
    return (
      <Spin indicator={<LoadingOutlined spin />} className="loader"></Spin>
    );
  if (error) return <ErrorComponent />;

  return (
    <>
      <Sidebar
        isAnnotationScreen
        bodyContent={
          <div className="floor-plan-detection-list">
            {filteredDetections.map((data) => (
              <div
                key={data?.id}
                ref={(el) => {
                  if (data?.id) {
                    cardRefs.current[data.id] = el;
                  }
                }}
                id={`detection-${data?.id}`}
                className="detection-card"
              >
                <FloorPlanDetectionCard
                  handleBoxClick={handleBoxClick}
                  data={data}
                  selectedId={selectedDetectionId}
                  filteredDetections={filteredDetections}
                />
              </div>
            ))}
          </div>
        }
        headerContent={
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            className="multiselect-filter"
            placeholder="Filter Areas"
            options={options}
            onChange={(values) => setSelectedTypes(values)}
          />
        }
      />
      <div className="vastu-report-page">
        <div>
          <VastuReportHeader />
        </div>
        {!isTabletBrowser(width) && (
          <AnnotationScreen
            selectedDetectionId={selectedDetectionId}
            data={filteredDetections as Detection[]}
            onBoxClick={handleBoxClick}
          />
        )}
        <div>
          {isTabletBrowser(width) && (
            <div className="floor-plan-detection-list">
              {filteredDetections.map((data, index) => (
                <FloorPlanDetectionCardMobile
                  key={index}
                  data={data}
                  filteredDetections={filteredDetections}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
